import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Image, AlertCircle } from 'lucide-react';

const DocumentViewerModal = ({ isOpen, onClose, documentUrl, documentType, documentName, originalUrl, isLoading }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen && documentUrl) {
      setLoading(true);
      setError(false);
    }
  }, [isOpen, documentUrl]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore body scrolling and position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  const handleDownload = async () => {
    if (documentUrl) {
      try {
        // Create a more descriptive filename
        const fileName = documentName 
          ? `${documentName.replace(/\s+/g, '_')}`
          : `${documentType?.replace(/([A-Z])/g, '_$1').toLowerCase()}_document`;
        
        // For presigned URLs, we need to fetch the blob and create a download
        const response = await fetch(documentUrl);
        const blob = await response.blob();
        
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback to direct link
        const link = document.createElement('a');
        link.href = documentUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const isImageDocument = (type) => {
    const imageTypes = ['driverPhoto', 'vehiclePhoto'];
    return imageTypes.includes(type) || 
           (documentUrl && (documentUrl.includes('.jpg') || documentUrl.includes('.jpeg') || documentUrl.includes('.png')));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-lg px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Close"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
          <div className="flex items-center space-x-2">
            {isImageDocument(documentType) ? (
              <Image className="h-5 w-5 text-gray-600" />
            ) : (
              <FileText className="h-5 w-5 text-gray-600" />
            )}
            <h2 className="text-lg font-semibold text-gray-900 capitalize">
              {documentName || documentType?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </h2>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleDownload}
            disabled={!documentUrl || isLoading || loading}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{loading ? 'Downloading...' : 'Download'}</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading && (
          <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
            <div className="loading-spinner w-8 h-8"></div>
            <p className="text-white text-lg">Loading document...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="w-full h-full flex flex-col items-center justify-center space-y-4 text-white">
            <AlertCircle className="h-16 w-16 text-red-400" />
            <h3 className="text-xl font-semibold">Failed to load document</h3>
            <p className="text-gray-300">The document could not be displayed. You can try downloading it instead.</p>
          </div>
        )}

        {!isLoading && !error && documentUrl && (
          <div className="w-full min-h-full flex items-start justify-center">
            {isImageDocument(documentType) ? (
              <img
                src={documentUrl}
                alt={documentType}
                className="max-w-[90vw] max-h-[80vh] w-auto h-auto object-contain shadow-2xl cursor-zoom-in hover:scale-105 transition-transform duration-200"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{
                  minWidth: '300px',
                  minHeight: '200px'
                }}
              />
            ) : (
              <iframe
                src={documentUrl}
                className="w-full h-full min-h-[80vh] border-none shadow-2xl bg-white"
                title={documentType}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white px-6 py-3 flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center space-x-4">
          <span>Document Type: {documentType}</span>
          {documentUrl && (
            <span className="text-xs text-gray-400 truncate max-w-md">
              {documentUrl.split('?')[0]}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span>Press ESC to close</span>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;